// src/components/layout/AppBar/FeedbackMenu.jsx
import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  TipsAndUpdatesOutlined,
  FeedbackOutlined,
  Add,
  CheckCircle,
  Block,
  HourglassEmpty,
  Help,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Hooks
import { useFeedback } from '../../../features/feedback/hooks/useFeedback';
import { useFeedbackStatus } from '../../../features/feedback/hooks/useFeedbackManagement';

// Components
import CreateFeedbackModal from '../../../features/feedback/components/CreateFeedbackModal';
import { dateHintUtils } from '../../common/DateWithHint';

const FeedbackMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    width: 380,
    maxHeight: 500,
    marginTop: theme.spacing(1),
  },
}));

const FeedbackHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const FeedbackList = styled(List)(({ theme }) => ({
  maxHeight: 340,
  overflow: 'auto',
  padding: 0,
}));

const FeedbackItem = styled(ListItem)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const getStatusIcon = (status) => {
  switch (status) {
    case 'FeedbackStatus.IMPLEMENTED':
      return <CheckCircle fontSize="small" />;
    case 'FeedbackStatus.WILL_NOT_IMPLEMENT':
      return <Block fontSize="small" />;
    case 'FeedbackStatus.PENDING_REVIEW':
      return <HourglassEmpty fontSize="small" />;
    case 'FeedbackStatus.NOT_DECIDED':
    default:
      return <Help fontSize="small" />;
  }
};

export default function FeedbackMenuComponent() {
  const { t } = useTranslation(['feedback', 'common']);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Hooks
  const { myFeedback, loading, fetchMyFeedback } = useFeedback();
  const { getImplementationColor, getImplementationLabel } = useFeedbackStatus();

  // Fetch user's feedback on mount
  useEffect(() => {
    fetchMyFeedback({ limit: 5 });
  }, [fetchMyFeedback]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
    // Refresh feedback when opening menu
    fetchMyFeedback({ limit: 5 });
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFeedbackClick = (feedback) => {
    navigate(`/feedback/${feedback.feedback_id}`);
    handleMenuClose();
  };

  const handleViewAllFeedback = () => {
    navigate('/feedback');
    handleMenuClose();
  };

  const handleCreateNew = () => {
    setCreateModalOpen(true);
    handleMenuClose();
  };

  const handleFeedbackCreated = () => {
    // Refresh the feedback list after creation
    fetchMyFeedback({ limit: 5 });
  };

  const isOpen = Boolean(anchorEl);

  // Count pending feedback (not decided status)
  const pendingCount = myFeedback.filter(
    (f) => f.status === 'NOT_DECIDED' || f.status === 'PENDING_REVIEW'
  ).length;

  // Helper function to get formatted date with hint
  const getSubmittedDateText = (createdAt) => {
    const { formattedDate, hint } = dateHintUtils.getDateWithHint(
      createdAt,
      t('feedback:menu.submitted', 'Submitted'),
      { dateFormat: 'd.M.yyyy' },
      t
    );
    
    // If there's a hint (like "2 days ago"), use it. Otherwise use the formatted date
    return hint || formattedDate;
  };

  // Create menu children array
  const menuChildren = [
    // Header
    <FeedbackHeader key="header">
      <Box>
        <Typography variant="h6" fontWeight="bold">
          {t('feedback:menu.title', 'My Feedback')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('feedback:menu.subtitle', 'Track your suggestions')}
        </Typography>
      </Box>
      <Tooltip title={t('feedback:menu.create_new', 'Create New Feedback')}>
        <IconButton
          color="primary"
          onClick={handleCreateNew}
          size="small"
        >
          <Add />
        </IconButton>
      </Tooltip>
    </FeedbackHeader>
  ];

  // Content
  if (loading) {
    menuChildren.push(
      <Box key="loading" sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  } else if (myFeedback.length === 0) {
    menuChildren.push(
      <Box key="empty" sx={{ p: 3, textAlign: 'center' }}>
        <FeedbackOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('feedback:menu.no_feedback', 'No feedback submitted yet')}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          onClick={handleCreateNew}
        >
          {t('feedback:menu.submit_first', 'Submit Your First Feedback')}
        </Button>
      </Box>
    );
  } else {
    menuChildren.push(
      <FeedbackList key="list">
        {myFeedback.map((feedback) => (
          <FeedbackItem
            key={feedback.feedback_id}
            onClick={() => handleFeedbackClick(feedback)}
          >
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" fontWeight="medium" noWrap sx={{ flex: 1 }}>
                    {feedback.title}
                  </Typography>
                </Box>
              }
              secondary={
                <Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                    <Chip
                      icon={getStatusIcon(feedback.status)}
                      label={getImplementationLabel(feedback.status)}
                      size="small"
                      color={getImplementationColor(feedback.status)}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                    {feedback.visibility === 'PUBLISHED' && (
                      <Chip
                        label={t('feedback:menu.published', 'Published')}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.disabled">
                    {t('feedback:menu.submitted', 'Submitted')} {getSubmittedDateText(feedback.created_at)}
                  </Typography>
                </Box>
              }
            />
          </FeedbackItem>
        ))}
      </FeedbackList>
    );
  }

  // Footer (only if there are feedback items)
  if (myFeedback.length > 0) {
    menuChildren.push(
      <Divider key="divider" />,
      <Box key="footer" sx={{ p: 1, display: 'flex', gap: 1 }}>
        <Button
          fullWidth
          variant="text"
          onClick={handleViewAllFeedback}
        >
          {t('feedback:menu.view_all', 'View All Feedback')}
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Tooltip title={t('feedback:menu.tooltip', 'Feedback & Suggestions')}>
        <IconButton
          color="inherit"
          onClick={handleMenuOpen}
          aria-label="feedback"
          aria-controls={isOpen ? 'feedback-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen ? 'true' : undefined}
        >
          <Badge badgeContent={pendingCount} color="info" max={99}>
            {/* A lightbulb, not a speech bubble: it sits next to the messages icon */}
            <TipsAndUpdatesOutlined />
          </Badge>
        </IconButton>
      </Tooltip>

      <FeedbackMenu
        id="feedback-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {menuChildren}
      </FeedbackMenu>

      <CreateFeedbackModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleFeedbackCreated}
      />
    </>
  );
}